import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast';
import { subscribeToAuth, getCurrentUser, wasOpenedFromResetLink, clearResetLink } from '../../lib/auth';

// Where the link in the password-reset email lands (useAuth.resetPassword
// sends it here). Supabase reads the token out of the address, signs the reader
// in on a temporary session and fires PASSWORD_RECOVERY; that event is the
// signal to ask for a new password. Nothing else is: someone who simply types
// /reset-password into the bar, or whose link has expired, is told so rather
// than shown a form that could not save.
//
// Same 8-character floor as sign-up (SignupPage.jsx).

const MIN_LENGTH = 8;
// Supabase reads the link first (it asks the server whether the token is good,
// then wipes it from the address bar), and only then announces the recovery,
// one tick later. Once its start-up has finished, wait this long for that
// announcement; if none came, nothing opened this page from a reset link.
const SETTLE_MS = 150;
// Backstop in case Supabase's start-up never finishes (its auth lock can be
// aborted on a very slow load).
const LINK_WAIT_MS = 8000;

// Supabase puts a problem with the link (expired, already used) in the address
// instead of a token. Read it before the client wipes it.
function linkProblem() {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return params.get('error_description') || params.get('error_code') || params.get('error') || '';
}

export default function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // 'checking' | 'form' | 'invalid'
  const [step, setStep] = useState('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    if (wasOpenedFromResetLink()) { setStep('form'); return undefined; }
    if (linkProblem()) { setStep('invalid'); return undefined; }

    const unsubscribe = subscribeToAuth((event) => {
      if (alive && event === 'PASSWORD_RECOVERY') setStep('form');
    });
    const giveUp = () => { if (alive) setStep(s => (s === 'checking' ? 'invalid' : s)); };
    // getCurrentUser resolves once Supabase has finished reading the address.
    getCurrentUser().then(() => setTimeout(giveUp, SETTLE_MS));
    const timer = setTimeout(giveUp, LINK_WAIT_MS);
    return () => { alive = false; unsubscribe(); clearTimeout(timer); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_LENGTH) { setError(`Password must be at least ${MIN_LENGTH} characters.`); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }

    setSaving(true);
    const { error: saveError } = await updatePassword(password);
    setSaving(false);
    if (saveError) { setError(saveError.message); return; }

    clearResetLink();
    addToast('Password updated. You are signed in.');
    navigate('/', { replace: true });
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-10)',
    border: '1px solid var(--brand-sand)', fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--brand-paper)',
  };
  const labelStyle = { fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-800)', display: 'block', marginBottom: 'var(--space-6)' };
  const headingStyle = { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--neutral-800)', margin: '0 0 6px' };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xxl)', fontFamily: 'var(--font-ui)' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        <div style={{ textAlign: 'center', marginBottom: 'var(--space-h3)' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <img src="/logo2.png" alt="Reanlo" style={{ width: 160, height: 48, objectFit: 'cover' }} />
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Reading beyond the Algorithm</p>
            </div>
          </Link>
        </div>

        <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', padding: 'var(--space-h3)', border: '1px solid var(--brand-sand)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>

          {step === 'checking' && (
            <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', margin: 'var(--space-none)', textAlign: 'center' }}>
              Checking your link...
            </p>
          )}

          {step === 'invalid' && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={headingStyle}>This link isn't working</h2>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 24px', lineHeight: 1.6 }}>
                It may have expired or already been used. Reset links work once and last 24 hours. Ask for a new one and we'll email it to you.
              </p>
              <Link to="/forgot-password" style={{ fontSize: 'var(--font-size-base)', color: 'var(--feedback-warning)', fontWeight: 600, textDecoration: 'none' }}>
                Send me a new link →
              </Link>
            </div>
          )}

          {step === 'form' && (
            <>
              <h2 style={headingStyle}>Choose a new password</h2>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 24px' }}>
                At least {MIN_LENGTH} characters.
              </p>

              {error && (
                <div role="alert" style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-10)', padding: '10px 14px', marginBottom: 'var(--space-xl)' }}>
                  <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--feedback-error)', margin: 'var(--space-none)' }}>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <label htmlFor="new-password" style={labelStyle}>New password</label>
                  <input
                    id="new-password" type="password" value={password} autoComplete="new-password" autoFocus required
                    onChange={e => setPassword(e.target.value)} style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: 'var(--space-xxl)' }}>
                  <label htmlFor="confirm-password" style={labelStyle}>Type it again</label>
                  <input
                    id="confirm-password" type="password" value={confirm} autoComplete="new-password" required
                    onChange={e => setConfirm(e.target.value)} style={inputStyle}
                  />
                </div>
                <button
                  type="submit" disabled={saving}
                  style={{ width: '100%', padding: '13px', borderRadius: 'var(--radius-10)', border: 'none', background: saving ? 'var(--neutral-400)' : 'var(--neutral-800)', color: 'var(--brand-paper)', fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}
                >
                  {saving ? 'Saving...' : 'Save new password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
